import { useState } from 'react'
import {
  Table, Group, TextInput, Title, Pagination, Badge, ActionIcon, Modal, Button,
  Stack, Card, Text, Switch, Checkbox, SimpleGrid, PasswordInput,
  Alert, Skeleton,
} from '@mantine/core'
import { IconSearch, IconEdit, IconTrash, IconPlus, IconAlertCircle, IconRefresh } from '@tabler/icons-react'
import { useForm, Controller } from 'react-hook-form'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { useSystemUsers } from '@/lib/swr'
import { useAuth } from '@/context/AuthContext'
import { ADMIN_PERMISSIONS } from '@/constants'
import dayjs from 'dayjs'

interface SystemUser {
  _id: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  role: string
  isSuperAdmin: boolean
  permissions: string[]
  status: string
  createdAt: string
}

interface FormData {
  firstName: string
  lastName: string
  email: string
  phone: string
  password: string
  isSuperAdmin: boolean
  permissions: string[]
}

const emptyForm: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
  isSuperAdmin: false,
  permissions: [],
}

export default function SystemUserList() {
  const { user } = useAuth()
  const isSuperAdmin = user?.isSuperAdmin ?? false

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ defaultValues: emptyForm })

  const watchIsSuperAdmin = watch('isSuperAdmin')
  const watchPermissions = watch('permissions')

  const queryParams: Record<string, string> = { page: String(page), limit: '20' }
  if (search) queryParams.search = search

  const { data: usersRes, isLoading: loading, error: swrError, mutate: reloadUsers } = useSystemUsers(queryParams)
  const users: SystemUser[] = ((usersRes as any)?.data ?? []) as SystemUser[]
  const pagination = {
    page,
    pages: (usersRes as any)?.pagination?.totalPages || (usersRes as any)?.pagination?.pages || 1,
  }
  const error = swrError ? (swrError instanceof Error ? swrError.message : 'Системийн хэрэглэгчдийг ачаалахад алдаа гарлаа') : null

  const load = () => { reloadUsers() }

  const openCreate = () => {
    setEditingId(null)
    reset(emptyForm)
    setModalOpen(true)
  }

  const openEdit = (u: SystemUser) => {
    setEditingId(u._id)
    reset({
      firstName: u.firstName,
      lastName: u.lastName,
      email: u.email,
      phone: u.phone || '',
      password: '',
      isSuperAdmin: u.isSuperAdmin,
      permissions: u.permissions || [],
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    reset(emptyForm)
  }

  const onSubmit = handleSubmit(async (form) => {
    if (!editingId && !form.password) {
      toast.error('Нууц үг заавал шаардлагатай')
      return
    }
    try {
      const data: Record<string, unknown> = {
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone || undefined,
        isSuperAdmin: form.isSuperAdmin,
        permissions: form.isSuperAdmin ? [] : form.permissions,
      }
      if (form.password) data.password = form.password

      if (editingId) {
        await api.admin.updateSystemUser(editingId, data)
        toast.success('Хэрэглэгч шинэчлэгдлээ')
      } else {
        await api.admin.createSystemUser({ ...data, email: form.email })
        toast.success('Хэрэглэгч үүсгэгдлээ')
      }
      closeModal()
      load()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  })

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    try {
      await api.admin.deleteSystemUser(deleteId)
      toast.success('Хэрэглэгч устгагдлаа')
      load()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Устгахад алдаа гарлаа')
    } finally {
      setDeleting(false)
      setDeleteId(null)
    }
  }

  const togglePermission = (key: string) => {
    const current = watchPermissions || []
    setValue(
      'permissions',
      current.includes(key) ? current.filter((p) => p !== key) : [...current, key],
      { shouldDirty: true }
    )
  }

  const colSpan = isSuperAdmin ? 8 : 7

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={2}>Системийн хэрэглэгчид</Title>
        {isSuperAdmin && (
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            Нэмэх
          </Button>
        )}
      </Group>

      <Group mb="md">
        <TextInput
          placeholder="Хайх..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => { setSearch(e?.currentTarget?.value ?? ''); setPage(1) }}
          w={300}
        />
      </Group>

      {error && (
        <Alert
          icon={<IconAlertCircle size={18} />}
          title="Алдаа"
          color="red"
          variant="light"
          mb="md"
        >
          <Group justify="space-between" align="center">
            <Text size="sm">{error}</Text>
            <Button size="xs" variant="light" color="red" leftSection={<IconRefresh size={14} />} onClick={load}>
              Дахин оролдох
            </Button>
          </Group>
        </Alert>
      )}

      <Card shadow="xs" padding={0} radius="md" withBorder>
        <Table.ScrollContainer minWidth={700}>
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr style={{ backgroundColor: 'var(--app-table-header)' }}>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={50}>#</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Нэр</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Имэйл</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Утас</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Төрөл</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Эрхүүд</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Огноо</Table.Th>
                {isSuperAdmin && (
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Үйлдэл</Table.Th>
                )}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <Table.Tr key={i}>
                    <Table.Td><Skeleton h={16} w={24} /></Table.Td>
                    <Table.Td><Skeleton h={16} /></Table.Td>
                    <Table.Td><Skeleton h={16} /></Table.Td>
                    <Table.Td><Skeleton h={16} w={90} /></Table.Td>
                    <Table.Td><Skeleton h={20} w={70} radius="xl" /></Table.Td>
                    <Table.Td><Skeleton h={20} w={100} radius="xl" /></Table.Td>
                    <Table.Td><Skeleton h={16} w={90} /></Table.Td>
                    {isSuperAdmin && <Table.Td><Skeleton h={28} w={60} radius="md" /></Table.Td>}
                  </Table.Tr>
                ))
              ) : (
                <>
                  {users.map((u, i) => (
                    <Table.Tr key={u._id}>
                      <Table.Td c="dimmed" fz="sm">{(pagination.page - 1) * 20 + i + 1}</Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {u.lastName ? `${u.lastName.charAt(0)}.` : ''}{u.firstName}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">{u.email}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{u.phone || '-'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light" color={u.isSuperAdmin ? 'red' : 'blue'} size="sm">
                          {u.isSuperAdmin ? 'Super Admin' : (u.role === 'admin' ? 'Admin' : 'Staff')}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {u.isSuperAdmin ? (
                          <Badge variant="light" color="red" size="sm">Бүх эрх</Badge>
                        ) : (
                          <Group gap={4}>
                            {(u.permissions || []).map((p) => {
                              const perm = ADMIN_PERMISSIONS.find((ap) => ap.key === p)
                              return (
                                <Badge key={p} variant="light" size="xs">
                                  {perm?.label || p}
                                </Badge>
                              )
                            })}
                            {(!u.permissions || u.permissions.length === 0) && (
                              <Text size="xs" c="dimmed">Эрх олгоогүй</Text>
                            )}
                          </Group>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">{dayjs(u.createdAt).format('YYYY.MM.DD')}</Text>
                      </Table.Td>
                      {isSuperAdmin && (
                        <Table.Td>
                          <Group gap={6}>
                            <ActionIcon variant="light" color="blue" radius="md" onClick={() => openEdit(u)}>
                              <IconEdit size={16} />
                            </ActionIcon>
                            <ActionIcon variant="light" color="red" radius="md" onClick={() => setDeleteId(u._id)}>
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Group>
                        </Table.Td>
                      )}
                    </Table.Tr>
                  ))}
                  {!loading && !error && users.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={colSpan}>
                        <Text ta="center" c="dimmed" py="xl">Админ хэрэглэгч олдсонгүй</Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>

        {pagination.pages > 1 && (
          <Group justify="center" p="md" style={{ borderTop: '1px solid var(--app-border)' }}>
            <Pagination total={pagination.pages} value={pagination.page} onChange={(p) => setPage(p)} />
          </Group>
        )}
      </Card>

      {/* Delete Confirmation */}
      <Modal
        opened={!!deleteId}
        onClose={() => setDeleteId(null)}
        title="Устгах"
        centered
        size="sm"
      >
        <Stack>
          <Text size="sm">Энэ хэрэглэгчийг устгахдаа итгэлтэй байна уу?</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteId(null)}>Болих</Button>
            <Button color="red" onClick={handleDelete} loading={deleting} disabled={deleting}>Устгах</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Create / Edit Modal */}
      <Modal
        opened={modalOpen}
        onClose={closeModal}
        title={editingId ? 'Хэрэглэгч засах' : 'Шинэ админ хэрэглэгч'}
        centered
        size="lg"
      >
        <form onSubmit={onSubmit}>
          <Stack>
            <Group grow>
              <Controller
                name="lastName"
                control={control}
                rules={{ validate: (v) => (v.trim() ? true : 'Овог шаардлагатай') }}
                render={({ field }) => (
                  <TextInput label="Овог *" {...field} required error={errors.lastName?.message} />
                )}
              />
              <Controller
                name="firstName"
                control={control}
                rules={{ validate: (v) => (v.trim() ? true : 'Нэр шаардлагатай') }}
                render={({ field }) => (
                  <TextInput label="Нэр *" {...field} required error={errors.firstName?.message} />
                )}
              />
            </Group>
            <Controller
              name="email"
              control={control}
              rules={{ validate: (v) => (v.trim() ? true : 'Имэйл шаардлагатай') }}
              render={({ field }) => (
                <TextInput label="Имэйл *" disabled={!!editingId} {...field} required error={errors.email?.message} />
              )}
            />
            <Group grow>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextInput label="Утас" {...field} />
                )}
              />
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <PasswordInput
                    label={editingId ? 'Шинэ нууц үг (хоосон бол өөрчлөхгүй)' : 'Нууц үг *'}
                    required={!editingId}
                    {...field}
                  />
                )}
              />
            </Group>
            <Controller
              name="isSuperAdmin"
              control={control}
              render={({ field }) => (
                <Switch
                  label="Super Admin (бүх эрхтэй)"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              )}
            />

            {!watchIsSuperAdmin && (
              <div>
                <Text size="sm" fw={500} mb="xs">Хандах эрхүүд</Text>
                <SimpleGrid cols={2}>
                  {ADMIN_PERMISSIONS.map((perm) => (
                    <Checkbox
                      key={perm.key}
                      label={perm.label}
                      checked={(watchPermissions || []).includes(perm.key)}
                      onChange={() => togglePermission(perm.key)}
                    />
                  ))}
                </SimpleGrid>
              </div>
            )}

            <Group justify="flex-end">
              <Button variant="default" onClick={closeModal} disabled={isSubmitting}>Болих</Button>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                {editingId ? 'Хадгалах' : 'Үүсгэх'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </div>
  )
}
